import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DonneesStructureBasePourPortes, PARAMETRES_PORTES_DEFAUT, ParametresPorte, ParametresPortes, PortesState, StructurePortes } from '../types';
import { Portes3DService } from '../services/Portes3DService';
import './PortesModule.css';

interface PortesModuleProps {
  donneesStructureBase: DonneesStructureBasePourPortes;
  onStructureChange?: (structure: StructurePortes | null) => void;
}

export const PortesModule: React.FC<PortesModuleProps> = ({ donneesStructureBase, onStructureChange }) => {
  const [state, setState] = useState<PortesState>({
    parametres: {
      ...PARAMETRES_PORTES_DEFAUT,
      portes: [
        {
          id: 'porte-1',
          nom: 'Porte 1',
          hauteur: PARAMETRES_PORTES_DEFAUT.hauteurDefaut,
          largeur: PARAMETRES_PORTES_DEFAUT.largeurDefaut,
          nombreRepetitions: PARAMETRES_PORTES_DEFAUT.nombreRepetitionsDefaut,
          formeArrondie: PARAMETRES_PORTES_DEFAUT.formeArrondieDefaut,
          actif: true
        }
      ]
    },
    isGenerating: false,
    editeurActif: true
  });

  const serviceRef = useRef<Portes3DService | null>(null);

  useEffect(() => {
    serviceRef.current = new Portes3DService();
    return () => {
      serviceRef.current = null;
    }
  }, []);

  const genererPortes = useCallback((params: ParametresPortes) => {
    if (!serviceRef.current) return;
    setState(prev => ({ ...prev, isGenerating: true, error: undefined }));
    try {
      const structure = serviceRef.current.genererPortes(params, donneesStructureBase);
      setState(prev => ({ ...prev, structure, isGenerating: false }));
      onStructureChange?.(structure);
    } catch (err) {
      console.error('Erreur génération des portes', err);
      const message = err instanceof Error ? err.message : 'Erreur inconnue';
      setState(prev => ({ ...prev, error: message, isGenerating: false }));
      onStructureChange?.(null);
    }
  }, [donneesStructureBase, onStructureChange]);

  // Références pour détecter les changements réels
  const previousParametresRef = useRef<ParametresPortes | null>(null);
  const previousStructureRef = useRef<DonneesStructureBasePourPortes | null>(null);

  // Régénération automatique lors des changements de paramètres OU de la structure de base
  useEffect(() => {
    const currentParams = state.parametres;
    const previousParams = previousParametresRef.current;
    const previousStructure = previousStructureRef.current;

    // Détection de changement des paramètres (inclut la liste des portes)
    const parametresChanged = !previousParams || JSON.stringify(previousParams) !== JSON.stringify(currentParams);

    // Détection de changement de la structure de base (forme, dimensions, montants, chemins de poutres)
    const s = donneesStructureBase;
    const structureChanged = !previousStructure ||
      previousStructure.forme !== s.forme ||
      previousStructure.largeur !== s.largeur ||
      previousStructure.longueur !== s.longueur ||
      previousStructure.hauteur !== s.hauteur ||
      previousStructure.nombreMontants !== s.nombreMontants ||
      (previousStructure.positionsMontants?.length || 0) !== (s.positionsMontants?.length || 0) ||
      (previousStructure.poutreHaute?.points?.length || 0) !== (s.poutreHaute?.points?.length || 0) ||
      (previousStructure.poutreBasse?.points?.length || 0) !== (s.poutreBasse?.points?.length || 0);

    if (!parametresChanged && !structureChanged) {
      return; // Aucun changement réel
    }

    // Sauvegarde des nouvelles références
    previousParametresRef.current = JSON.parse(JSON.stringify(currentParams));
    previousStructureRef.current = JSON.parse(JSON.stringify(s));

    if (currentParams.actif) {
      genererPortes(currentParams);
    } else {
      setState(prevState => ({ ...prevState, structure: undefined }));
      onStructureChange?.(null);
    }
  }, [donneesStructureBase, state.parametres, genererPortes, onStructureChange]);

  const majPorte = useCallback((id: string, patch: Partial<ParametresPorte>) => {
    setState(prev => ({
      ...prev,
      parametres: {
        ...prev.parametres,
        portes: prev.parametres.portes.map(p => p.id === id ? { ...p, ...patch } : p)
      }
    }));
  }, []);

  const ajouterPorte = useCallback(() => {
    const n = state.parametres.portes.length + 1;
    const nouvelle: ParametresPorte = {
      id: `porte-${n}`,
      nom: `Porte ${n}`,
      hauteur: state.parametres.hauteurDefaut,
      largeur: state.parametres.largeurDefaut,
      nombreRepetitions: state.parametres.nombreRepetitionsDefaut,
      formeArrondie: state.parametres.formeArrondieDefaut,
      actif: true
    };
    setState(prev => ({
      ...prev,
      parametres: { ...prev.parametres, portes: [...prev.parametres.portes, nouvelle] }
    }));
  }, [state.parametres]);

  const retirerPorte = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      parametres: { ...prev.parametres, portes: prev.parametres.portes.filter(p => p.id !== id) }
    }));
  }, []);

  return (
    <div className="portes-module">
      <div className="portes-header">
        <strong>Module 8: Les Portes</strong>
        <label className="portes-switch">
          <input
            type="checkbox"
            checked={state.parametres.actif}
            onChange={(e) => setState(prev => ({ ...prev, parametres: { ...prev.parametres, actif: e.target.checked } }))}
          />
          <span>Activer</span>
        </label>
      </div>

      {state.isGenerating && <div className="portes-status">⏳ Génération des portes...</div>}
      {state.error && <div className="portes-error">❌ {state.error}</div>}

      <div className="portes-controls">
        <div className="portes-field">
          <label>Hauteur défaut (m)</label>
          <input
            type="number"
            step={0.01}
            value={state.parametres.hauteurDefaut}
            onChange={(e) => setState(prev => ({ ...prev, parametres: { ...prev.parametres, hauteurDefaut: parseFloat(e.target.value) || 0 } }))}
          />
        </div>
        <div className="portes-field">
          <label>Largeur défaut (m)</label>
          <input
            type="number"
            step={0.01}
            value={state.parametres.largeurDefaut}
            onChange={(e) => setState(prev => ({ ...prev, parametres: { ...prev.parametres, largeurDefaut: parseFloat(e.target.value) || 0 } }))}
          />
        </div>
        <div className="portes-field">
          <label>Nombre de répétitions défaut</label>
          <input
            type="number"
            min={1}
            step={1}
            value={state.parametres.nombreRepetitionsDefaut}
            onChange={(e) => setState(prev => ({ ...prev, parametres: { ...prev.parametres, nombreRepetitionsDefaut: Math.max(1, parseInt(e.target.value, 10) || 1) } }))}
          />
        </div>
        <div className="portes-field">
          <label>Forme arrondie (défaut)</label>
          <input
            type="checkbox"
            checked={state.parametres.formeArrondieDefaut}
            onChange={(e) => setState(prev => ({ ...prev, parametres: { ...prev.parametres, formeArrondieDefaut: e.target.checked } }))}
          />
        </div>
      </div>

      <div className="portes-list">
        {state.parametres.portes.map((p) => (
          <div key={p.id} className="portes-item">
            <div className="portes-row" style={{ justifyContent: 'space-between' }}>
              <strong>{p.nom}</strong>
              <label className="portes-switch">
                <input type="checkbox" checked={p.actif} onChange={() => majPorte(p.id, { actif: !p.actif })} />
                <span>Activer</span>
              </label>
            </div>

            <div className="portes-row">
              <div className="portes-field" style={{ flex: 1 }}>
                <label>Nombre de répétitions</label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={p.nombreRepetitions}
                  onChange={(e) => majPorte(p.id, { nombreRepetitions: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                />
              </div>
              <div className="portes-field" style={{ flex: 1 }}>
                <label>Forme arrondie</label>
                <input
                  type="checkbox"
                  checked={p.formeArrondie}
                  onChange={(e) => majPorte(p.id, { formeArrondie: e.target.checked })}
                />
              </div>
            </div>

            <div className="portes-row">
              <div className="portes-field" style={{ flex: 1 }}>
                <label>Hauteur (m)</label>
                <input type="number" step={0.01} value={p.hauteur} onChange={(e) => majPorte(p.id, { hauteur: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="portes-field" style={{ flex: 1 }}>
                <label>Largeur (m)</label>
                <input type="number" step={0.01} value={p.largeur} onChange={(e) => majPorte(p.id, { largeur: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>

            {/* Blocs legacy supprimés: montants, décalageSol, sensOuverture */}

            <div className="portes-actions">
              <button className="portes-button" onClick={() => majPorte(p.id, { actif: !p.actif })}>{p.actif ? 'Désactiver' : 'Activer'}</button>
              <button className="portes-button" onClick={() => retirerPorte(p.id)}>Supprimer</button>
            </div>
          </div>
        ))}

        <div className="portes-actions">
          <button className="portes-button" onClick={ajouterPorte}>+ Ajouter une porte</button>
          <button className="portes-button" onClick={() => state.parametres.actif && genererPortes(state.parametres)}>Régénérer</button>
        </div>
      </div>
    </div>
  );
}

export default PortesModule;